import { FC } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Avatar, Image, Tooltip, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ProcSearchResponse } from '@/shared/api';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Col, ImagePlaceholder } from '@/shared/ui';
import { intialsFromName } from '@/shared/utils';

type Props = {
  proc: ProcSearchResponse;
};

const { Title } = Typography;

export const DiscoverContent: FC<Props> = ({ proc }) => {
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { isAuthenticated } = useAuth0();

  const handleAvatarClick = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | undefined,
  ) => {
    e?.stopPropagation();
    navigate(
      isAuthenticated
        ? `/dashboard/discover/organization/${proc.organizationId}`
        : `/organization/${proc.organizationId}`,
    );
  };

  return (
    <>
      <Wrapper
        $borderHoverColor={themeConfig[theme].colors.borderHover}
        onClick={() =>
          navigate(
            isAuthenticated
              ? `/dashboard/proc/${proc.groupId}/preview`
              : `/procs/${proc.groupId}/preview`,
            {
              state: { from: location.pathname },
            },
          )
        }
        style={{
          backgroundColor: themeConfig[theme].colors.contentBg,
          borderColor: themeConfig[theme].colors.border,
        }}
      >
        <Header>
          <Tooltip title={proc.organizationName || proc.organization?.name}>
            {proc.organizationUploadedImageLink ? (
              <Avatar
                src={proc.organizationUploadedImageLink}
                size={32}
                onClick={handleAvatarClick}
                style={{
                  border: `1px solid ${themeConfig[theme].colors.disabled}`,
                }}
              />
            ) : (
              <Avatar
                icon={
                  <Initials>
                    {intialsFromName(
                      proc.organizationName || proc.organization?.name,
                    )}
                  </Initials>
                }
                size={32}
                onClick={handleAvatarClick}
                style={{
                  backgroundColor: themeConfig[theme].colors.disabled,
                  border: `1px solid ${themeConfig[theme].colors.disabled}`,
                }}
              />
            )}
          </Tooltip>
        </Header>
        <Title
          level={5}
          ellipsis={{ rows: 2 }}
          style={{
            lineHeight: 1.3,
            paddingInline: 8,
            height: 42,
            overflow: 'hidden',
            marginTop: 8,
          }}
        >
          {proc.title}
        </Title>
        <Content>
          {proc.imageLink || proc.uploadedImageLink ? (
            <Image
              preview={false}
              src={proc.imageLink || proc.uploadedImageLink}
              fallback="data:image/png;base64,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"
              style={{ objectFit: 'cover', height: 140, borderRadius: 8 }}
            />
          ) : (
            <ImagePlaceholder style={{ height: 140 }} />
          )}
        </Content>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div<{ $borderHoverColor: string }>`
  border-width: 1px;
  border-style: solid;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  cursor: pointer;
  transition: all 0.25s ease-in-out;
  &:hover {
    border-color: ${({ $borderHoverColor }) => $borderHoverColor} !important;
  }
`;

const Header = styled.div`
  padding: 8px;
  padding-bottom: 0;
  color: rgba(0, 0, 0, 0.88);
  width: 100%;
  font-size: 12px;
`;

const Content = styled(Col)`
  width: 100%;
  gap: 15px;
  padding: 8px;
  & .ant-image {
    width: 100%;
  }
`;

const Initials = styled.div`
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
`;
